import { DifficultyLevel } from '../interfaces/question.interface';
import { ZoomRound } from '../interfaces/zoom-quest.interface';

/**
 * Difficulty ↔ digit count. More digits = more zoom levels = deeper dive.
 *   EASY   → 3 digits (hundreds)     e.g. 428
 *   MEDIUM → 4 digits (thousands)    e.g. 6,687
 *   HARD   → 5 digits (ten-thousands) e.g. 43,905
 */
const DIGITS: Record<DifficultyLevel, number> = {
  [DifficultyLevel.EASY]: 3,
  [DifficultyLevel.MEDIUM]: 4,
  [DifficultyLevel.HARD]: 5
};

/** Random integer in [lo, hi] inclusive. */
function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

/**
 * Pick a target with `digits` digits that is *interesting* to zoom to:
 * not a round multiple of 100 (so the tens/ones actually matter) and not
 * sitting on the very first or last segment of the widest window.
 */
function pickTarget(digits: number): number {
  const min = Math.pow(10, digits - 1);       // 100, 1000, 10000
  const max = Math.pow(10, digits) - 1;        // 999, 9999, 99999
  for (let tries = 0; tries < 40; tries++) {
    const n = randInt(min, max);
    if (n % 100 === 0) continue;               // avoid trivially round numbers
    return n;
  }
  return randInt(min, max);
}

function toRound(difficulty: DifficultyLevel, idx: number): ZoomRound {
  const digits = DIGITS[difficulty];
  const target = pickTarget(digits);
  return {
    id: `zoom-${idx}-${target}`,
    target,
    digits,
    rootLo: 0,
    rootHi: Math.pow(10, digits),
    rootStep: Math.pow(10, digits - 1),
    difficulty
  };
}

/**
 * Build a session's rounds on a gentle easy→hard ramp. The first couple of
 * turns are 3-digit, the middle 4-digit, and the tail 5-digit, so the dive
 * gets one level deeper as confidence builds.
 */
export function buildZoomQuestRounds(count: number): ZoomRound[] {
  const n = Math.max(1, count);
  const out: ZoomRound[] = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : i / (n - 1);       // 0 → 1 across the session
    let difficulty: DifficultyLevel;
    if (t < 0.34) difficulty = DifficultyLevel.EASY;
    else if (t < 0.72) difficulty = DifficultyLevel.MEDIUM;
    else difficulty = DifficultyLevel.HARD;
    out.push(toRound(difficulty, i));
  }
  return out;
}
